import { useEffect, useRef } from 'react'
import { useMap } from '@vis.gl/react-google-maps'

export interface PolylineProps {
  path: google.maps.LatLngLiteral[]
  strokeColor?: string
  strokeWeight?: number
  strokeOpacity?: number
  zIndex?: number
  /** Polilínea invisible y gruesa usada como zona de click sobre la ruta. */
  clickable?: boolean
  onClick?: (e: google.maps.PolyMouseEvent) => void
}

/**
 * Polilínea imperativa sobre `google.maps.Polyline`. La implementamos nosotros
 * (en vez de depender del export del paquete) para controlar el ciclo de vida y
 * el handler de click, replicando el comportamiento que teníamos con Leaflet.
 */
export function Polyline({
  path,
  strokeColor = '#1A7A4C',
  strokeWeight = 4,
  strokeOpacity = 0.95,
  zIndex,
  clickable = false,
  onClick,
}: PolylineProps) {
  const map = useMap()
  const lineRef = useRef<google.maps.Polyline | null>(null)

  useEffect(() => {
    if (!map) return
    const line = new google.maps.Polyline()
    line.setMap(map)
    lineRef.current = line
    return () => {
      line.setMap(null)
      lineRef.current = null
    }
  }, [map])

  useEffect(() => {
    lineRef.current?.setOptions({
      path,
      strokeColor,
      strokeWeight,
      strokeOpacity,
      zIndex,
      clickable: clickable || !!onClick,
    })
  }, [path, strokeColor, strokeWeight, strokeOpacity, zIndex, clickable, onClick])

  useEffect(() => {
    const line = lineRef.current
    if (!line || !onClick) return
    const listener = line.addListener('click', onClick)
    return () => listener.remove()
  }, [onClick])

  return null
}
